import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { marketingApi } from "../api/marketing";
import { formatApiError } from "../lib/errors";

// One-click unsubscribe landing. Reached from the link inside marketing emails.
// We POST immediately on mount: any modern mailbox prefetches the link target
// to neutralise tracking pixels, so requiring a click here would mean some
// users get "Confirm" prompts they never see. RFC 8058 / Gmail List-Unsubscribe
// already expects a one-click flow — we mirror that contract on the web page.
export default function Unsubscribe() {
  const { t } = useTranslation("landing");
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState("loading"); // "loading" | "success" | "error"
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setError(t("unsubscribe.missingToken"));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await marketingApi.unsubscribe(token);
        if (cancelled) return;
        setEmail(resp.data?.email || "");
        setStatus("success");
      } catch (err) {
        if (cancelled) return;
        setStatus("error");
        setError(formatApiError(err, t("unsubscribe.errorFallback")));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <div className="w-full max-w-md feature-card p-8 rounded-lg text-center">
        <Link to="/" className="block mb-8">
          <span className="font-clash text-2xl font-bold text-white">TrapMan</span>
        </Link>

        {status === "loading" && (
          <>
            <h1 className="font-clash text-2xl font-bold text-white mb-4">{t("unsubscribe.loadingTitle")}</h1>
            <p className="text-sm text-[#A1A1AA]">{t("unsubscribe.loadingBody")}</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="text-4xl mb-4">&#10003;</div>
            <h1 className="font-clash text-2xl font-bold text-white mb-3">{t("unsubscribe.successTitle")}</h1>
            <p className="text-sm text-[#A1A1AA] mb-6">
              {email ? <>{t("unsubscribe.successEmailPrefix")} <span className="text-white">{email}</span>{t("unsubscribe.successEmailSuffix")}</> : t("unsubscribe.successNoEmail")}
              {" "}{t("unsubscribe.successEssential")}
            </p>
            <p className="text-xs text-[#A1A1AA]/70 mb-6">
              {t("unsubscribe.changedMind")}{" "}
              <Link to="/dashboard/settings" className="underline hover:text-[#FF5500]">{t("unsubscribe.accountSettings")}</Link>{t("unsubscribe.changedMindSuffix")}
            </p>
            <Link to="/" className="text-[#FF5500] hover:text-[#FF7733] text-sm">{t("unsubscribe.backToTrapMan")}</Link>
          </>
        )}

        {status === "error" && (
          <>
            <h1 className="font-clash text-2xl font-bold text-white mb-3">{t("unsubscribe.errorTitle")}</h1>
            <p className="text-sm text-red-400 mb-6">{error}</p>
            <Link to="/" className="text-[#FF5500] hover:text-[#FF7733] text-sm">{t("unsubscribe.backToTrapMan")}</Link>
          </>
        )}
      </div>
    </div>
  );
}
